import React, { useEffect, useState } from 'react';
import { DashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const formatNumber = (value) => {
  if (value === null || value === undefined) return '0';
  return Number(value).toLocaleString('no-NO');
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleDateString('no-NO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTimeFromNow = (value) => {
  if (!value) return '';
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.round(diffMs / 60000);
  if (diffMinutes < 1) return 'akkurat nå';
  if (diffMinutes < 60) return `for ${diffMinutes} min siden`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `for ${diffHours} t siden`;
  return date.toLocaleString('no-NO', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CompanyPage = () => {
  const { selectedCompany, permissions } = useAuth();
  const companyId = selectedCompany?.id || null;
  // Use permission system instead of manual isOwner check
  const isOwner = permissions.canViewCompany;
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  useEffect(() => {
    if (!companyId || !isOwner) {
      setSummary(null);
      return;
    }
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await DashboardApi.getCompanySummary(companyId);
        if (!isMounted) return;
        setSummary(response);
      } catch (err) {
        if (!isMounted) return;
        setError(err.message || 'Kunne ikke hente selskapstavlen.');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [companyId, isOwner]);


  if (!companyId) {
    return (
      <div className="page">
        <div className="card">
          <h1>Selskap</h1>
          <p>Velg et selskap for å se informasjon.</p>
        </div>
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="page">
        <div className="card">
          <h1>Selskap</h1>
          <p>Kun eiere har tilgang til selskapstavlen.</p>
        </div>
      </div>
    );
  }

  const metrics = summary?.metrics;
  const activity = summary?.activity || [];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <p className="page-eyebrow">Selskap</p>
          <h1>{selectedCompany?.name || 'Selskap'}</h1>
          <p className="page-subtitle">Oversikt over team, bots og bruk for dette selskapet.</p>
        </div>
      </div>

      {error && (
        <div className="card">
          <p>⚠️ {error}</p>
        </div>
      )}

      {loading ? (
        <div className="card">
          <p>Laster selskapstall…</p>
        </div>
      ) : summary ? (
        <>
          {/* Core Metrics */}
          <section className="card-grid">
            <article className="card kpi-card">
              <header className="card-header">
                <span className="card-title">Bots</span>
              </header>
              <p className="card-value">{formatNumber(metrics?.activeBots || 0)}</p>
              <p className="card-meta">{formatNumber(metrics?.bots || 0)} totalt</p>
            </article>
            <article className="card kpi-card">
              <header className="card-header">
                <span className="card-title">Medlemmer</span>
              </header>
              <p className="card-value">{formatNumber(metrics?.members || 0)}</p>
            </article>
            <article className="card kpi-card">
              <header className="card-header">
                <span className="card-title">Samtaler</span>
              </header>
              <p className="card-value">{formatNumber(metrics?.conversationsThisMonth || 0)}</p>
              <p className="card-meta">denne måneden ({formatNumber(metrics?.totalConversations || 0)} totalt)</p>
            </article>
            <article className="card kpi-card">
              <header className="card-header">
                <span className="card-title">Integrasjoner</span>
              </header>
              <p className="card-value">{formatNumber(metrics?.integrations || 0)}</p>
              <p className="card-meta">{formatNumber(metrics?.enabledTools || 0)} verktøy aktivert</p>
            </article>
            <article className="card kpi-card">
              <header className="card-header">
                <span className="card-title">Flyter</span>
              </header>
              <p className="card-value">{formatNumber(metrics?.activeFlows || 0)}</p>
              <p className="card-meta">{formatNumber(metrics?.flowRunsThisMonth || 0)} kjørt denne mnd</p>
            </article>
            <article className="card kpi-card">
              <header className="card-header">
                <span className="card-title">Kunnskapsbase</span>
              </header>
              <p className="card-value">{formatNumber(metrics?.knowledgeEntries || 0)}</p>
              <p className="card-meta">oppføringer</p>
            </article>
          </section>


          {/* Recent Activity from Company Summary */}
          {activity.length > 0 && (
            <section className="card">
              <header className="card-header">
                <h2>Siste aktivitet</h2>
              </header>
              <ul className="timeline">
                {activity.map((item) => (
                  <li key={item.id} className="timeline-item">
                    <div className="timeline-dot" data-severity={item.severity} />
                    <div>
                      <h3>{item.title}</h3>
                      {item.body && <p>{item.body}</p>}
                      <span className="timeline-time">{formatDateTimeFromNow(item.createdAt)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Company Info Footer */}
          <section className="card card-muted">
            <p className="company-info-footer">
              Selskap opprettet {formatDate(summary?.company?.createdAt)}
              {summary?.company?.meta?.organizationNumber && (
                <> • Org.nr: {summary.company.meta.organizationNumber}</>
              )}
            </p>
          </section>
        </>
      ) : (
        <div className="card">
          <p>Ingen data tilgjengelig.</p>
        </div>
      )}
    </div>
  );
};

export default CompanyPage;
